import { useState } from 'react';
import { Shield, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { useSubscriptionStore } from '../store/subscriptionStore';
import type { SubscriptionTierCode } from '../types';

interface SubscriptionSelectorProps {
  onSelected: (tier: SubscriptionTierCode) => void;
  showUpgradeOnly?: boolean;
  onBack?: () => void;
}

export function SubscriptionSelector({ onSelected, showUpgradeOnly = false, onBack }: SubscriptionSelectorProps) {
  const { plans, currentTier, error } = useSubscriptionStore();
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSelectTier = async (tier: SubscriptionTierCode) => {
    // If it's the free tier, proceed without payment
    if (tier === 'free') {
      onSelected(tier);
      return;
    }
    
    setIsProcessing(true);
    setProcessingError(null);
    
    try {
      // For paid tiers, use the subscription store's upgradeTier method
      // which will redirect to Stripe checkout
      const result = await useSubscriptionStore.getState().upgradeTier(tier);
      
      // If we get here, it means the redirect didn't happen
      // which could indicate an error
      if (!result) {
        throw new Error('Failed to initiate checkout process');
      }
      
      // This code will likely not run due to the redirect
      onSelected(tier);
    } catch (err) {
      setProcessingError('Failed to process subscription. Please try again.');
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Project Types
          </button>
        )}

        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {showUpgradeOnly ? 'Upgrade Your Plan' : 'Choose Your Plan'}
          </h2>
          <p className="text-xl text-gray-600">
            Select the plan that best fits your energy design needs
          </p>
        </div>

        {(error || processingError) && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center text-red-700">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              {error || processingError}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(plans).map(([tier, plan]) => {
            if (showUpgradeOnly && tier === 'free') return null;
            
            return (
              <div
                key={tier}
                className={`bg-white rounded-xl p-6 shadow-md border-2 transition-all ${
                  currentTier === tier
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300 cursor-pointer'
                }`}
                onClick={() => !isProcessing && currentTier !== tier && handleSelectTier(tier as SubscriptionTierCode)}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    {tier === 'free' ? 'Free' : 
                     tier === 'business' ? 'Business' : 
                     tier === 'enterprise' ? 'Enterprise' : 'Ultimate'}
                  </h3>
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>

                <div className="mb-6">
                  <p className="text-3xl font-bold text-gray-900">
                    SAR {plan.priceMonthly}
                    <span className="text-base font-normal text-gray-500">/mo</span>
                  </p>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isProcessing && currentTier !== tier) {
                      handleSelectTier(tier as SubscriptionTierCode);
                    }
                  }}
                  disabled={currentTier === tier || isProcessing}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                    currentTier === tier
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : isProcessing
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {currentTier === tier
                    ? 'Current Plan'
                    : isProcessing
                    ? 'Processing...'
                    : tier === 'free'
                    ? 'Downgrade to Free'
                    : `Upgrade to ${tier === 'business' ? 'Business' : tier === 'enterprise' ? 'Enterprise' : 'Ultimate'}`}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-10 bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Plan Comparison</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feature</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Free</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enterprise</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ultimate</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Maximum Projects</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">5</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">10</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Unlimited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">System Size Limit</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">20kW</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">50kW</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">250kW</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2000kW</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Support Level</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Basic</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Priority</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Dedicated</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">White Glove</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Custom Reporting</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="text-red-500">✕</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="text-green-500">✓</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="text-green-500">✓</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="text-green-500">✓</span>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">AI Optimization</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="text-red-500">✕</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="text-green-500">✓</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="text-green-500">✓</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="text-green-500">✓</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
